import React from 'react';
import { X, XCircle } from 'lucide-react';

import { ApiKey } from '../../types/apiKey';
import { Spinner } from '../../design-system';

interface RevokeApiKeyModalProps {
  isOpen: boolean;
  apiKey: ApiKey | null;
  isRevoking: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const RevokeApiKeyModal: React.FC<RevokeApiKeyModalProps> = ({
  isOpen,
  apiKey,
  isRevoking,
  onClose,
  onConfirm,
}) => {
  if (!isOpen || !apiKey) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={() => {
          if (!isRevoking) onClose();
        }}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-ui-background-secondary rounded-lg shadow-xl w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-ui-border-primary">
            <div className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              <h2 className="text-lg font-semibold text-ui-text-primary">
                Revoke API Key
              </h2>
            </div>
            <button
              onClick={onClose}
              disabled={isRevoking}
              className="p-1 rounded hover:bg-ui-background-tertiary transition-colors disabled:opacity-50"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-ui-text-tertiary" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            <p className="text-sm text-ui-text-secondary">
              You are about to revoke{' '}
              <span className="font-medium text-ui-text-primary">{apiKey.name}</span>{' '}
              (<span className="font-mono">{apiKey.key_prefix}…</span>).
            </p>
            <p className="text-sm text-ui-text-secondary">
              Revoking will immediately disable this key. Any integrations using it will stop working.
            </p>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isRevoking}
                className="px-4 py-2 text-sm font-medium text-ui-text-secondary
                           hover:bg-ui-background-tertiary rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={isRevoking}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700
                           disabled:opacity-50 rounded-lg transition-colors flex items-center gap-2 disabled:cursor-not-allowed"
              >
                {isRevoking ? (
                  <>
                    <Spinner size={16} tone="current" />
                    Revoking...
                  </>
                ) : (
                  'Revoke key'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RevokeApiKeyModal;
